import { useEffect, useState } from 'react';

// Subscribes to a CSS media query and returns whether it currently
// matches. Used to swap between mobile / desktop variants of the same
// piece of UI at runtime — e.g. forcing card-grid layout on mobile
// where the dense table is awkward to scroll through.
//
// `fallback` is the value returned before the first measurement
// resolves (SSR, missing matchMedia). Defaults to `true` because every
// caller today uses this hook to decide "am I on desktop?" — assuming
// desktop on uncertainty avoids the May 15 regression where the
// Property Portal flashed grid for one render after back-navigation
// when matchMedia briefly returned a stale `false`.
export function useMediaQuery(query: string, fallback = true): boolean {
  const [matches, setMatches] = useState(() => {
    if (typeof window === 'undefined') return fallback;
    try {
      return window.matchMedia(query).matches;
    } catch {
      return fallback;
    }
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    let mq: MediaQueryList;
    try {
      mq = window.matchMedia(query);
    } catch {
      return;
    }
    const onChange = () => setMatches(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [query]);

  return matches;
}
